/* eslint-disable @typescript-eslint/no-explicit-any */
import { ENDPOINTS } from '@/util'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useGetFilterSelections } from '@/catalog-apps/hooks/useGetFilterSelections'
import { ListCardsResponse } from './types'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useGetPageFilters } from '../../AppPage2/Filter/useGetPageFilters'
import { useIsPageBuilder } from '@/catalog-apps/hooks/useIsPageBuilder'
import { TComponentType } from '../../AppPage2/types'

// Set to true to use mock data, false to use actual data
const USE_MOCK_DATA = false

interface GetListCardsParams {
  appKey?: string
  contentId?: string
  projectId?: number
  filterSelections: Record<string, any>[]
  isPageBuilder?: boolean
}

const getListCards = async ({ appKey, contentId, projectId, filterSelections, isPageBuilder }: GetListCardsParams) => {
  if (!appKey || !contentId || !projectId) {
    throw new Error('Missing required parameters')
  }
  if (isPageBuilder) {
    return Promise.resolve({
      ...(await import('@/catalog-apps/mock-data/list-cards/list-cards-response.json')).json,
    } as ListCardsResponse)
  }
  if (USE_MOCK_DATA) {
    return Promise.resolve(
      (await import('@/catalog-apps/mock-data/list-cards/list-cards-response.json')).json as ListCardsResponse
    )
  }

  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }

  const url = `${ENDPOINTS.CC_DOTNET}/content`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      app_id: appKey,
      content_id: contentId,
      project_id: projectId,
      filters: filterSelections,
    }),
  })

  const data = (await response.json()) as ListCardsResponse
  if (response.ok) {
    return data
  }
  throw new Error('Failed to fetch listCards data')
}

export function useGetListCards(contentId: string, tileFilterSelections: Record<string, any>[]) {
  const isPageBuilder = useIsPageBuilder()
  const {
    data: { defaultSelections },
  } = useGetPageFilters()
  const { filterSelections } = useGetFilterSelections()
  const { appKey, projectId } = useAppContext()
  const filterSelectionsArray =
    filterSelections.length > 0
      ? [...filterSelections, ...tileFilterSelections]
      : [...defaultSelections, ...tileFilterSelections]

  const componentType: TComponentType = 'list-cards'
  return useSuspenseQuery({
    queryKey: [contentId, componentType, appKey, projectId, filterSelectionsArray],
    queryFn: () =>
      getListCards({
        appKey,
        contentId,
        projectId,
        filterSelections: filterSelectionsArray,
        isPageBuilder,
      }),
  })
}
